import { useEffect, useRef, useState } from "react";
import { Button, PocketRow } from "@/components/ui";
import {
  chooseScreenRecordingDirectory,
  getScreenRecordingSettings,
  resetScreenRecordingDirectory,
  type ScreenRecordingSettings,
} from "@/desktop/screen-recording-settings";
import { useT } from "@/i18n";
import { settingsConnectionsMessages } from "@/i18n/messages/settings-connections";
import { isTauri } from "@/lib/is-tauri";
import { SettingsFieldError } from "./settings-text-field";

export function ScreenRecordingFolderRow() {
  const t = useT(settingsConnectionsMessages);
  const [settings, setSettings] = useState<ScreenRecordingSettings | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const writing = useRef(false);
  const mounted = useRef(false);
  const revision = useRef(0);

  useEffect(() => {
    mounted.current = true;
    const refresh = () => {
      if (!isTauri() || writing.current) return;
      const request = ++revision.current;
      void getScreenRecordingSettings().then(
        (value) => {
          if (mounted.current && revision.current === request) {
            setSettings(value);
            setError(null);
          }
        },
        () => {
          if (mounted.current && revision.current === request)
            setError(t("recordingFolderReadFailed"));
        },
      );
    };
    refresh();
    window.addEventListener("focus", refresh);
    return () => {
      mounted.current = false;
      revision.current += 1;
      window.removeEventListener("focus", refresh);
    };
  }, [t]);

  async function change(reset: boolean) {
    if (writing.current) return;
    writing.current = true;
    revision.current += 1;
    setSaving(true);
    setError(null);
    try {
      const next = await (reset
        ? resetScreenRecordingDirectory()
        : chooseScreenRecordingDirectory());
      if (mounted.current && next) setSettings(next);
    } catch (failure) {
      if (mounted.current)
        setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      writing.current = false;
      if (mounted.current) setSaving(false);
    }
  }

  if (!isTauri()) return null;
  return (
    <PocketRow
      testId="settings-row-screen-recordings"
      title={t("recordingFolder")}
      secondLine={
        settings ? (
          <span title={settings.directory}>{settings.directory}</span>
        ) : (
          t("recordingFolderDefault")
        )
      }
      trailing={
        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            disabled={saving}
            onClick={() => void change(false)}
            data-testid="screen-recording-folder-choose"
          >
            {saving ? t("recordingFolderSaving") : t("recordingFolderChange")}
          </Button>
          {settings && settings.directory !== settings.defaultDirectory ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={() => void change(true)}
              data-testid="screen-recording-folder-reset"
            >
              {t("recordingFolderUseDefault")}
            </Button>
          ) : null}
        </div>
      }
      below={
        <>
          <p
            className="pt-1 text-appkit-small text-muted"
            data-testid="screen-recording-folder-hint"
          >
            {t("recordingFolderHint")}
          </p>
          <SettingsFieldError
            error={error}
            testId="screen-recording-folder-error"
          />
        </>
      }
    />
  );
}
